import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useRuntimeVariables } from 'app-kit'
import { Button } from 'designSystem/components/Button'
import { Input } from 'designSystem/components/Input'
import { useGeckoAuth } from 'features/auth'
import {
  Callout,
  Hint,
  Panel,
  PanelTitle,
  Row,
} from 'features/sentinelDesign/designStyles'
import { useSentinelEntry } from 'features/sentinelDesign/useDurableDesign'
import type { SentinelMonitorConfig } from 'features/sentinels/types'
import { FC, useEffect, useState } from 'react'
import styled from 'styled-components'

const CADENCES = [
  { label: msg`Every hour`, minutes: 60 },
  { label: msg`Every 5 min`, minutes: 5 },
] as const

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 0.9rem;
`

const Label = styled.label`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast?.secondaryText ?? 'inherit'};
`

const NumberInput = styled(Input)`
  max-width: 8rem;
`

const sameConfig = (
  a: SentinelMonitorConfig | null,
  b: SentinelMonitorConfig | null
): boolean =>
  !!a &&
  !!b &&
  a.enabled === b.enabled &&
  a.cadence_minutes === b.cadence_minutes &&
  a.method === b.method &&
  a.band_k === b.band_k

export interface MonitorTabProps {
  entryId: string
  site: string
  target?: string
  /** Fitted method keys, offered as the served-method choices. */
  methodKeys: string[]
  methodLabels: Record<string, string>
}

/**
 * View and edit a sentinel's recurring-monitor config (`data.monitor`). Disabled
 * by default; nothing runs until it is turned on. The band is a multiplier on
 * the served method's residual scale — a breach is `|residual| > band_k *
 * err_scale`, the same definition the fitted model uses.
 */
export const MonitorTab: FC<MonitorTabProps> = ({
  entryId,
  site,
  target,
  methodKeys,
  methodLabels,
}) => {
  const { i18n, t } = useLingui()

  const { entry, mutate } = useSentinelEntry(entryId)
  const saved = entry?.data.monitor ?? null
  const { authorizedAxios } = useGeckoAuth()
  const { FIM_SERVICE_URL } = useRuntimeVariables()

  const [draft, setDraft] = useState<SentinelMonitorConfig | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The band field is a free-text buffer so intermediate values ("0", "0.")
  // survive keystrokes; the draft's numeric band_k only updates on a valid parse.
  const [bandText, setBandText] = useState('')

  // Seed the editable draft once the entry loads. A sentinel fitted before the
  // monitor config existed has no `data.monitor`, so fall back to sensible
  // defaults the first save will persist.
  useEffect(() => {
    if (draft === null && entry) {
      const seeded = saved ?? {
        enabled: false,
        cadence_minutes: 60,
        target: target ?? null,
        method: methodKeys[0] ?? null,
        band_k: 2,
      }
      setDraft(seeded)
      setBandText(String(seeded.band_k))
    }
  }, [entry, saved, draft, target, methodKeys])

  if (!draft)
    return (
      <Hint>
        <Trans>Loading monitor config…</Trans>
      </Hint>
    )

  const patch = (fields: Partial<SentinelMonitorConfig>) =>
    setDraft((prev) => (prev ? { ...prev, ...fields } : prev))

  const dirty = !sameConfig(draft, saved)

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      await authorizedAxios.patch(
        `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(
          site
        )}/sentinel/${encodeURIComponent(entryId)}/monitor`,
        {
          enabled: draft.enabled,
          cadence_minutes: draft.cadence_minutes,
          method: draft.method,
          band_k: draft.band_k,
        }
      )
      await mutate()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Panel aria-label={t`Monitor`}>
      <Row>
        <PanelTitle>
          <Trans>Monitor</Trans>
        </PanelTitle>
        <Button
          variant={draft.enabled ? 'primaryRaised' : 'secondary'}
          onClick={() => patch({ enabled: !draft.enabled })}>
          {draft.enabled ? t`Enabled` : t`Disabled`}
        </Button>
      </Row>
      <Hint>
        <Trans>
          When enabled, this sentinel is scored on the cadence below and a case
          is opened whenever the target leaves its band. Off by default —
          nothing runs until you turn it on.
        </Trans>
      </Hint>

      <Field>
        <Label>
          <Trans>Cadence</Trans>
        </Label>
        <Row>
          {CADENCES.map((c) => (
            <Button
              key={c.minutes}
              variant={
                draft.cadence_minutes === c.minutes
                  ? 'primaryRaised'
                  : 'secondary'
              }
              onClick={() => patch({ cadence_minutes: c.minutes })}>
              {i18n._(c.label)}
            </Button>
          ))}
        </Row>
      </Field>

      <Field>
        <Label>
          <Trans>Served method — whose residuals are watched</Trans>
        </Label>
        <Row>
          {methodKeys.map((key) => (
            <Button
              key={key}
              variant={draft.method === key ? 'primaryRaised' : 'secondary'}
              onClick={() => patch({ method: key })}>
              {methodLabels[key] ?? key}
            </Button>
          ))}
        </Row>
      </Field>

      <Field>
        <Label htmlFor='monitor-band-k'>
          <Trans>
            Band width (x residual scale) — breach when |residual| exceeds this
          </Trans>
        </Label>
        <NumberInput
          id='monitor-band-k'
          type='number'
          min={0.1}
          step={0.1}
          value={bandText}
          onChange={(event) => {
            const text = event.target.value
            setBandText(text)
            const next = Number(text)
            if (text.trim() !== '' && Number.isFinite(next) && next > 0) {
              patch({ band_k: next })
            }
          }}
        />
        <Hint>
          <Trans>Target </Trans>
          {target ?? '—'}
          <Trans>
            . Lower is tighter (more sensitive); the fit seeded this from the
            model&apos;s own band.
          </Trans>
        </Hint>
      </Field>

      {error && <Callout $tone='error'>{error}</Callout>}
      {saved?.last_run_at && (
        <Hint>
          <Trans>Last scored </Trans>
          {new Date(saved.last_run_at).toLocaleString()}.
        </Hint>
      )}

      <Row>
        <Button
          variant='primaryRaised'
          disabled={!dirty || saving}
          onClick={() => void save()}>
          {saving ? t`Saving…` : dirty ? t`Save` : t`Saved`}
        </Button>
      </Row>
    </Panel>
  )
}
